import { EurekaPackageType, type IPackageType } from '@eureka/services/src/apis/common.api.types';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { packageInfoDeferred } from '@eureka/utils/package-info';
import { createSharedComposable, tryOnBeforeMount } from '@vueuse/core';
import { computed, ref } from 'vue';
import type { ISubscriptionListDTO } from '../types';
import { PAID_STATUS, ESubscriptionStatus } from '../types';
import _ from 'lodash';
import { cancelSubscription, getInvoices, getSubscriptionList, resumeSubscription, createPaymentSession, checkPaymentStatus } from '../apis/stripe-api';
import type { Account } from '@patsnap-utils/biz';
import { controlDeferred } from '@eureka/utils/control';
import type { IControl } from '@eureka/utils/control';
import { checkJpEnv } from '../utils/check-jp-env';

export const usePlg = createSharedComposable(() => {
    const packageInfo = ref<IPackageType[]>([])
    const control = ref<IControl>()
    const isLogined = ref<boolean>(false)

    // 检测套餐付款状态
    function getPaidStatus(eureka_packages: string[], beforeCheck?: (pkgs: EurekaPackageType[]) => boolean) {
        // 获取已购商业套餐
        const commercialPkgs = _.filter(packageInfo.value, (pkg) => pkg.package_type === 'COMMERCIAL').map(pkg => pkg.eureka_package) as EurekaPackageType[];

        // 前置检查，如果已购套餐中存在比当前高的版本，则跳出检查，返回低版本标识
        if (beforeCheck?.(commercialPkgs)) {
            return PAID_STATUS.LOW_VERSION;
        }
        // 获取交集套餐
        const intersectionPkgs = _.intersection(eureka_packages, commercialPkgs);
        return intersectionPkgs.length > 0 ? PAID_STATUS.PAID : PAID_STATUS.UNPAID;
    }

    const subscriptionList = ref<ISubscriptionListDTO[]>([]);

    /**
     * 获取用户订阅列表
     */
    async function fetchSubscriptionList() {
        isLogined.value = await isUserLogin();
        const res = isLogined.value ? await getSubscriptionList() : [];
        subscriptionList.value = res.filter(item => [ESubscriptionStatus.active, ESubscriptionStatus.trialing, ESubscriptionStatus.canceled].includes(item.subscription_status));
    }

    /**
     *
     * @returns 查看账单
     */
    async function handleViewInvoices(subscription_id: string) {
        if (!subscription_id) return;
        const res = await getInvoices({
            subscription_id,
            return_url: window.location.href,
        });
        if (!res) return;
        window.open(res, '_blank', 'noopener,noreferrer');
    }

    /**
     * 取消订阅
     * @returns
     */
    async function handleCancelPlan(subscription_id: string, reason: string, otherReason?: string) {
        if (!subscription_id) return;
        await cancelSubscription({
            subscription_id,
            reason,
            ...(otherReason ? { remark: otherReason } : {}),
        });
    }

    /**
     * 恢复订阅
     * @returns
     */
    async function handleResumePlan(subscription_id: string) {
        if (!subscription_id) return;
        await resumeSubscription({
            subscription_id,
        });
    }

    /**
     * 是否取消了订阅
     */
    const isCanceled = computed(() => {
        return !!subscriptionList.value?.cancel_at;
    })

    const account = ref<Account | null>(null);

    const isPersonal = computed(() => {
        return !account.value?._info?.company_id;
    })

    const isCompany = computed(() => {
        return account.value?._info?.company_id;
    })

    const isJPEnv = computed(() => {
        return checkJpEnv();
    })

    // 以下是套餐
    const isPro = computed(() => {
        return packageInfo.value.some(pkg => pkg.eureka_package === EurekaPackageType.RD_PERSONAL_COMMERCIAL);
    })

    const isFreemium = computed(() => {
        return control.value?.freemium;
    })

    const isEnterprise = computed(() => {
        return !isFreemium.value && !isPro.value && isLogined.value;
    })

    function handleCheckPaymentStatus(timestamp: string) {
        return checkPaymentStatus(timestamp);
    }

    tryOnBeforeMount(async() => {
        isLogined.value = await isUserLogin();
        if (isLogined.value) {
            packageInfo.value = await packageInfoDeferred.promise;
        }
        control.value = await controlDeferred.promise;
        account.value = await accountDeferred.promise;
    })

    return {
        getPaidStatus,
        packageInfo,
        fetchSubscriptionList,
        handleViewInvoices,
        handleCancelPlan,
        handleResumePlan,
        isPersonal,
        isEnterprise,
        isCanceled,
        subscriptionList,
        createPaymentSession,
        isPro,
        account,
        control,
        isFreemium,
        handleCheckPaymentStatus,
        isLogined,
        isCompany,
        isJPEnv,
    }
})
